import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subject } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { AuthMutationResponse, Mutation, RefreshTokenGQL } from "../graphql/graphql";
import { NavigationService } from "@/core/services/navigation.service";
import { Operation } from '@apollo/client/core';

export const STORAGE_AUTH = 'authData';
export interface AuthData {
  accessToken: string;
  refreshToken: string;
  userId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private _authDataSubject = new Subject<AuthData>();
  private _loggedInSubject = new BehaviorSubject<boolean>(false);

  public readonly authData$ = this._authDataSubject.asObservable();
  public readonly loggedIn$ = this._loggedInSubject.asObservable();

  public get currentAuthData(): AuthData | null {
    const stored = localStorage.getItem(STORAGE_AUTH);
    return stored ? JSON.parse(stored) as AuthData : null;
  }

  public constructor(
    private readonly _injector: Injector,
    private readonly _navigator: NavigationService,
  ) {
    if (localStorage.getItem(STORAGE_AUTH)) {
      const authData: AuthData = JSON.parse(localStorage.getItem(STORAGE_AUTH) as string);
      this.setAuthData(authData);
    }
  }

  public login(authData: AuthData): void {
    this.setAuthData(authData);
    this._navigator.navigateToLandingPage();
  }

  public refreshToken(): Observable<AuthData | never> {
    if (!this.currentAuthData) {
      this.logout();
      return EMPTY;
    }
    const apollo = this._injector.get<RefreshTokenGQL>(RefreshTokenGQL);

    return apollo.mutate(
      {
        input: {
          refreshToken: this.currentAuthData.refreshToken,
        },
      },
    ).pipe(
      catchError(error => {
        this.logout();
        return EMPTY;
      }),
      map(next => {
        const mut = next.data as Mutation;
        const data: AuthMutationResponse = mut.auth?.refreshAccessToken!;
        const newAuthData: AuthData = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          userId: data.user.id,
        };
        return newAuthData;
      }),
      tap(next => this.setAuthData(next)),
    );
  }

  public logout(): void {
    this.setAuthData(null);
    this._navigator.navigateToLogin();
  }

  private setAuthData(authData: AuthData | null): void {
    this.storeToLocalStorage(authData);
    if (authData) {
      this._authDataSubject.next(authData);
    }
    this._loggedInSubject.next(!!authData);
  }

  private storeToLocalStorage(authData: AuthData | null): void {
    if (authData) {
      localStorage.setItem(STORAGE_AUTH, JSON.stringify(authData));
    } else {
      localStorage.removeItem(STORAGE_AUTH);
    }
  }
}
